import React from 'react';
import Card from 'components/card';

import {createColumnHelper, flexRender, getCoreRowModel, useReactTable} from '@tanstack/react-table';
import {WalletTransactionItem} from '../../../../stores/types';
import {formatCurrency, longDateFormatStr} from '../../../../stores/JSHelper';
import {MdChevronRight, MdRefresh} from 'react-icons/md';
import {useNavigate} from 'react-router-dom';
import {isCentralAccount} from '../../../../stores/Constants';
import UseAccountStore from '../../../../stores/DataStore';
import UseLoginStore from '../../../../stores/LoginStore';
import Psp from '../../../../components/widget/Psp';

function RecentTransactionsTable(props: {transactions: WalletTransactionItem[]}) {

  const {transactions} = props;
  const navigate = useNavigate();
  const {account, setAccount, fetchSummary, refresh} = UseAccountStore();
  const {user} = UseLoginStore();
  const refreshPage = () => {
    refresh(true);
    fetchSummary(true);
  };

  const columns = [
    columnHelper.accessor('transactionDate', {
      id: 'date',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">Date</p>,
      cell: (info: any) => (
        <div className="flex items-center">
          <p className="ml-3 text-sm font-bold text-navy-700 dark:text-white">{longDateFormatStr(info.getValue())}</p>
        </div>
      ),
    }),
    columnHelper.accessor((row) => row, {
      id: 'partyName',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">Party</p>,
      cell: (info) => (
        <div className="text-sm font-bold text-navy-700 dark:text-white">
          <div className="text-sm font-bold text-navy-700 dark:text-white">
            <div>
              <button className="hover:bg-indigo-100" onClick={() => setAccount(info.getValue().accountId)}>
                {info.getValue().accountName}
              </button>
            </div>
            <div>
              <small>
                {isCentralAccount(account.accountId) && <span>{info.getValue().requesterAccountName} | </span>}
                {info.getValue().partyId} | {info.getValue().updatedBy}
              </small>
            </div>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor('utr', {
      id: 'UTR',
      header: () => <p className="text-center text-sm font-bold text-gray-600 dark:text-white">UTR</p>,
      cell: (info) => <p className="text-center text-sm font-bold text-navy-700 dark:text-white">
        <div className='flex flex-row items-center justify-center'>
          <div className='mr-2' >
            <Psp empty pspType={info.row.original.pspType} />
          </div>
          {info.getValue()}
        </div>
      </p>,
    }),
    columnHelper.accessor('credit', {
      id: 'credit',
      header: () => <p className="text-right text-sm font-bold text-gray-600 dark:text-white">Credit</p>,
      cell: (info) => (
        <p className="text-right text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() === 0 ? '-' : formatCurrency(info.getValue(), 'INR', false)}
        </p>
      ),
    }),
    columnHelper.accessor('debit', {
      id: 'debit',
      header: () => <p className="text-right text-sm font-bold text-gray-600 dark:text-white">Debit</p>,
      cell: (info) => (
        <p className="text-right text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() === 0 ? '-' : formatCurrency(info.getValue(), 'INR', false)}
        </p>
      ),
    }),
    columnHelper.accessor('closingBalance', {
      id: 'closingBalance',
      header: () => <p className="text-right text-sm font-bold text-gray-600 dark:text-white">Balance</p>,
      cell: (info) => <p className="text-right text-sm font-bold text-navy-700 dark:text-white">{formatCurrency(info.getValue(), 'INR', false)}</p>,
    }),
  ]; // eslint-disable-next-line

  const table = useReactTable({
    data: transactions,
    columns,
    enableSorting: false,
    state: {},
    onSortingChange: () => false,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card extra={'w-full h-full sm:overflow-auto'}>
      <header className="relative px-6 pt-10">
        <div className="text-xl font-bold text-navy-700 dark:text-white">Recent Wallet Transactions</div>
        <div className='mt-8 flex flex-row items-center justify-between gap-3' >
          <div className=" flex flex-row items-center gap-3">
            {
              user && (user.role === "admin" || user.role === "editor" ) ? (
                <button
                  onClick={() => navigate('/admin/ops')}
                  className="flex flex-row items-center rounded-xl bg-gray-100 px-4 py-3 text-xs font-medium text-navy-700 transition duration-200 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30"
                >
                  Ops Tasks <MdChevronRight className="text-lg" />
                </button>
              ) : null
            }
          
          </div>
          <div className=" flex flex-row items-center gap-3">
            <button
              onClick={() => refreshPage()}
              className="flex flex-row items-center rounded-xl bg-gray-100 px-4 py-3 text-xs font-medium text-navy-700 transition duration-200 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30"
            >
              Refresh <MdRefresh className="text-lg" />
            </button>
            {isCentralAccount(account.accountId) && (
              <>
                <button
                  onClick={() => navigate('/admin/active-accounts')}
                  className="flex flex-row items-center rounded-xl bg-gray-100 px-4 py-3 text-xs font-medium text-navy-700 transition duration-200 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30"
                >
                  Active Accounts <MdChevronRight className="text-lg" />
                </button>
                <button
                  onClick={() => navigate('/admin/pending-cashfree-transactions')}
                  className="flex flex-row items-center rounded-xl bg-gray-100 px-4 py-3 text-xs font-medium text-navy-700 transition duration-200 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30"
                >
                  Transfers <MdChevronRight className="text-lg" />
                </button>
              </>
            )}
            <button
              onClick={() => navigate('/admin/bank-transactions')}
              className="flex flex-row items-center rounded-xl bg-gray-100 px-4 py-3 text-xs font-medium text-navy-700 transition duration-200 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30"
            >
              Bank Transactions <MdChevronRight className="text-lg" />
            </button>
            <button
              onClick={() => navigate('/admin/ledger')}
              className="flex flex-row items-center rounded-xl bg-gray-100 px-4 py-3 text-xs font-medium text-navy-700 transition duration-200 hover:bg-gray-200 active:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/30"
            >
              Wallet Ledger <MdChevronRight className="text-lg" />
            </button>
          </div>
        </div>
      </header>

      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={() => false}
                      className="cursor-pointer border-b-[1px] border-gray-200 px-6 pb-2 pr-4 pt-4 text-start"
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: '',
                          desc: '',
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {table.getRowModel().rows.length > 0 && (
            <tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id} className="odd:bg-gray-50">
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id} className="min-w-[150px] border-white/0 px-2 py-3 pr-4">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          )}
          {table.getRowModel().rows.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={100} className="py-5 text-center">
                  No recent transactions
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </Card>
  );
}

export default RecentTransactionsTable;
const columnHelper = createColumnHelper<WalletTransactionItem>();
